import { HttpErrorResponse } from '@angular/common/http';

import { ViveApiError } from '../shared/vive-api/vive-api.error';

/** The `name` the API gives a password refused because the account uses SSO. */
export const SSO_SIGN_IN_REQUIRED_ERROR_NAME = 'SsoSignInRequiredException';

// Owner-approved copy for a password the API refuses because the account signs
// in with single sign-on.
export const SSO_SIGN_IN_REQUIRED_COPY =
  'Your organization signs in with single sign-on. Use Continue with SSO instead.';
export const SSO_SIGN_IN_ELSEWHERE_COPY =
  'Your organization signs in with single sign-on. Sign in on the VIVE login page with Continue with SSO.';
export const SSO_NO_PASSWORD_TO_RESET_COPY =
  "Your organization signs in with single sign-on, so there's no VIVE password to reset. Use Continue with SSO on the sign-in page.";
export const SSO_USER_RESET_REFUSED_COPY =
  "This user signs in with single sign-on, so a password reset can't be sent.";

/**
 * The API's status, or 0 when there was none: the client raises a TypeError
 * when nothing answered, and a ViveApiError without a status when it could not
 * read the answer.
 */
export function statusOf(error: unknown): number {
  return error instanceof ViveApiError ? (error.httpStatusCode ?? 0) : 0;
}

/** The API's own wording: a plain-text body, or the `message` of a JSON one. */
export function messageOf(error: unknown): string | null {
  const body = error instanceof ViveApiError ? error.cause : null;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (body && typeof body === 'object' && 'message' in body) {
    const { message } = body;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return null;
}

/**
 * Whether the API refused a password because the account signs in with single
 * sign-on. Recognized by the error's `name` alone, which the API keeps stable;
 * the body arrives on `cause` from the generated client and on `error` from
 * HttpClient.
 */
export function isSsoSignInRequired(error: unknown): boolean {
  const body =
    error instanceof ViveApiError
      ? error.cause
      : error instanceof HttpErrorResponse
        ? error.error
        : null;
  return (
    typeof body === 'object' &&
    body !== null &&
    'name' in body &&
    body.name === SSO_SIGN_IN_REQUIRED_ERROR_NAME
  );
}
